/**
 * Settings Page
 * User settings and configuration options
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '../store/uiStore';
import { ChangePasswordModal } from '../components/ui/ChangePasswordModal';
import { LanguageSwitcher } from '../components/ui/LanguageSwitcher';

interface SettingsPageProps {
  onBack: () => void;
}

interface SettingsItemProps {
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
  isDark: boolean;
  testId?: string;
}

function SettingsItem({ icon, title, onClick, isDark, testId }: SettingsItemProps) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center justify-between p-4 rounded-xl transition-colors ${
        isDark
          ? 'bg-slate-900/50 hover:bg-slate-800'
          : 'bg-slate-100 hover:bg-slate-200'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={isDark ? 'text-slate-400' : 'text-slate-500'}>
          {icon}
        </div>
        <span className={isDark ? 'text-slate-200' : 'text-slate-700'}>
          {title}
        </span>
      </div>
      {/* ChevronRight Icon */}
      <svg
        className={`w-5 h-5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}

// Lock Icon
function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  );
}

export function SettingsPage({ onBack }: SettingsPageProps) {
  const { t } = useTranslation();
  const { theme } = useUIStore();
  const isDark = theme === 'dark';
  const isLight = theme === 'light';
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  return (
    <div className="px-4 py-6 space-y-4">
      {/* Back Button */}
      <button
        onClick={onBack}
        data-testid="settings-back-button"
        className={`flex items-center gap-2 mb-4 ${
          isDark ? 'text-slate-400 hover:text-slate-300' : 'text-slate-600 hover:text-slate-700'
        }`}
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        <span>{t('common.back')}</span>
      </button>

      {/* Page Title */}
      <h1
        className={`text-2xl font-bold mb-6 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}
        data-testid="settings-title"
      >
        {t('pages.settings.title')}
      </h1>

      {/* Settings List */}
      <div className="space-y-3">
        <SettingsItem
          icon={<LockIcon className="w-5 h-5" />}
          title={t('pages.settings.changePassword')}
          onClick={() => setIsPasswordModalOpen(true)}
          isDark={isDark}
          testId="settings-change-password"
        />

        {/* Language Switcher */}
        <div className={`rounded-xl ${isLight ? 'bg-slate-100' : 'bg-slate-900/50'}`}>
          <LanguageSwitcher />
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />
    </div>
  );
}
